import React from "react";
import { lazy, useState, useEffect, useMemo, Suspense } from "react";
import { useApp } from "../context/AppContext";
import { motion, AnimatePresence } from "motion/react";
import ProtectedRoute from './ProtectedRoute';
import Header from "./Header";
const HomeView = lazy(() => import("./HomeView"));
import ProductCard from "./ProductCard";
const ProductDetailsModal = lazy(() => import("./ProductDetailsModal"));
import CartDrawer from "./CartDrawer";
const CheckoutModal = lazy(() => import("./CheckoutModal"));
import BottomNav from "./BottomNav";
const DealsView = lazy(() => import("./DealsView"));
const OrdersView = lazy(() => import("./OrdersView"));
const TrackOrderView = lazy(() => import("./TrackOrderView"));
const SearchView = lazy(() => import("./SearchView"));
const CategoriesView = lazy(() => import("./CategoriesView"));
const AdminPanelView = lazy(() => import("./AdminPanelView"));
import MenuDrawer from "./MenuDrawer";
const AIAdvisor = lazy(() => import("./AIAdvisor"));
const FreeDeliveryView = lazy(() => import("./FreeDeliveryView"));
import ProductCardSkeleton from "./ProductCardSkeleton";
import { Product } from "../types";
import { loginWithGoogle } from "../lib/firebase";
import { SlidersHorizontal } from "lucide-react";

export default function PriyofyDashboard() {
  const {
    activeTab,
    setActiveTab,
    allProducts,
    isAdmin,
    isEditor,
    isOwner,
    lang,
    selectedProduct,
    setSelectedProduct: setSelectedProductGlobal,
  } = useApp();
  const [selectedCat, setSelectedCat] = useState("all");
  const [selectedSkinType, setSelectedSkinType] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [selectedPriceRange, setSelectedPriceRange] = useState("all");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  // Monitor global scroll events to show/hide header and nav bar based on scroll direction (Facebook-like)
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only respond if the user has scrolled more than 5px to avoid jitter/sensitivity issues
      if (Math.abs(currentScrollY - lastScrollY) < 5) {
        return;
      }

      // If scrolling down (user pulls content up) and passed top area, hide the bars
      // If scrolling up (user pulls content down), show the bars immediately
      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        setIsScrolling(true);
      } else {
        setIsScrolling(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const availableBrands = useMemo(() => {
    const brands = new Set<string>();
    (allProducts || []).forEach((p) => {
      if (p?.brand) {
        brands.add(p.brand.trim());
      }
    });
    return ["all", ...Array.from(brands)];
  }, [allProducts]);

  const isCmsAuthorized = isAdmin || isEditor || isOwner;

  const [showExitConfirm, setShowExitConfirm] = React.useState(false);
  const allowExit = React.useRef(false);
  const isPopStateNavigating = React.useRef(false);
  const lastStateRef = React.useRef({ activeTab, cartOpen, checkoutOpen, menuOpen, hasProduct: !!selectedProduct });
  const backPressCountRef = React.useRef<number>(0);
  const backPressTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  // 1. Initialize history trap to prevent immediate exit
  useEffect(() => {
    if (!window.history.state || window.history.state.priyofyInit !== true) {
      window.history.replaceState({ priyofyTrap: true, priyofyInit: true }, '');
      window.history.pushState({ 
        priyofyState: true, 
        priyofyInit: true,
        activeTab, cartOpen, checkoutOpen, menuOpen, hasProduct: !!selectedProduct 
      }, '');
    }
  }, []);

  // 2. Main popstate listener for back button handling
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      const state = e.state;
      if (state && state.priyofyTrap) {
        // User reached the root trap by pressing back repeatedly
        backPressCountRef.current += 1;
        
        if (backPressTimerRef.current) {
          clearTimeout(backPressTimerRef.current);
        }
        
        backPressTimerRef.current = setTimeout(() => {
          backPressCountRef.current = 0;
        }, 1500);

        if (backPressCountRef.current >= 2) {
          setShowExitConfirm(true);
          backPressCountRef.current = 0;
          if (backPressTimerRef.current) clearTimeout(backPressTimerRef.current);
        }
        // Trap them again
        const currentState = { activeTab, cartOpen, checkoutOpen, menuOpen, hasProduct: !!selectedProduct };
        window.history.pushState({ 
          priyofyState: true,
          priyofyInit: true,
          ...currentState
        }, '');
      } else if (state && state.priyofyState) {
        // Navigating back within internal history stack
        isPopStateNavigating.current = true;
        
        if (activeTab !== state.activeTab) setActiveTab(state.activeTab || 'home');
        if (cartOpen !== state.cartOpen) setCartOpen(state.cartOpen || false);
        if (checkoutOpen !== state.checkoutOpen) setCheckoutOpen(state.checkoutOpen || false);
        if (menuOpen !== state.menuOpen) setMenuOpen(state.menuOpen || false);
        if (!state.hasProduct && selectedProduct) setSelectedProductGlobal?.(null);
        
        lastStateRef.current = {
          activeTab: state.activeTab,
          cartOpen: state.cartOpen,
          checkoutOpen: state.checkoutOpen,
          menuOpen: state.menuOpen,
          hasProduct: state.hasProduct
        };
      } else {
        // Edge case fallback
        window.history.replaceState({ priyofyTrap: true, priyofyInit: true }, '');
        window.history.pushState({ 
          priyofyState: true, 
          priyofyInit: true,
          activeTab: 'home', cartOpen: false, checkoutOpen: false, menuOpen: false, hasProduct: false
        }, '');
        setShowExitConfirm(true);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (selectedProduct) setSelectedProductGlobal?.(null);
        else if (checkoutOpen) setCheckoutOpen(false);
        else if (cartOpen) setCartOpen(false);
        else if (menuOpen) setMenuOpen(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeTab, cartOpen, checkoutOpen, menuOpen, selectedProduct, setActiveTab, setSelectedProductGlobal]);

  // 3. Push state when user navigates forward
  useEffect(() => {
    const currentState = { activeTab, cartOpen, checkoutOpen, menuOpen, hasProduct: !!selectedProduct };
    const isChanged = JSON.stringify(currentState) !== JSON.stringify(lastStateRef.current);
    
    if (isChanged) {
      if (!isPopStateNavigating.current) {
        window.history.pushState({
          priyofyState: true,
          priyofyInit: true,
          ...currentState
        }, '');
      }
      lastStateRef.current = currentState;
    }
    // Always reset the flag after a state change cycle
    isPopStateNavigating.current = false;
  }, [activeTab, cartOpen, checkoutOpen, menuOpen, selectedProduct]);

  // Keep setSelectedProduct for compatibility with existing code
  const setSelectedProduct = (prod: Product | null) => {
    setSelectedProductGlobal?.(prod);
  };

  // Toggle tab view smoothly if user is no longer CMS authorized
  useEffect(() => {
    if (activeTab === "admin" && !isCmsAuthorized) {
      setActiveTab("orders");
    }
  }, [isCmsAuthorized, activeTab, setActiveTab]);

  // Note: Removed the buggy activeTab effect that forcefully closed modals on popstate.
  // The bottom navigation tab clicks handle state correctly through the normal react flow.

  // Synchronize category selection from dynamic global event triggers (such as Menu Drawer)
  useEffect(() => {
    const handleSelectCatEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setSelectedCat(customEvent.detail);
      }
    };
    window.addEventListener("select-category", handleSelectCatEvent, {
      passive: true,
    });
    return () => {
      window.removeEventListener("select-category", handleSelectCatEvent);
    };
  }, []);

  const filtered = useMemo(() => {
    return allProducts.filter((p) => {
      // 1. Category Filter
      if (selectedCat !== "all" && p.categoryEn !== selectedCat) return false;

      // 2. Skin Type Filter
      if (selectedSkinType !== "all") {
        const pSkin = (p.skinType || "All").toLowerCase();
        const targetSkin = selectedSkinType.toLowerCase();
        if (pSkin !== targetSkin) return false;
      }

      // 3. Brand Filter
      if (selectedBrand !== "all") {
        const pBrand = (p.brand || "Priyofy").toLowerCase();
        const targetBrand = selectedBrand.toLowerCase();
        if (pBrand !== targetBrand) return false;
      }

      // 4. Price range Filter
      if (selectedPriceRange !== "all") {
        if (selectedPriceRange === "u500" && p.price >= 500) return false;
        if (
          selectedPriceRange === "500to1500" &&
          (p.price < 500 || p.price > 1500)
        )
          return false;
        if (selectedPriceRange === "o1500" && p.price <= 1500) return false;
      }
      return true;
    });
  }, [
    allProducts,
    selectedCat,
    selectedSkinType,
    selectedBrand,
    selectedPriceRange,
  ]);

  return (
    <div
      id="priyofy-root"
      className="min-h-[100dvh] w-full flex-1 flex flex-col relative"
    >
      {/* Clean, Ultra-fast Background */}
      <div className="fixed inset-0 pointer-events-none -z-10 bg-neutral-50 dark:bg-[#0A0A0C]" />

      <Header
        onCartClick={() => setCartOpen(prev => !prev)}
        onMenuClick={() => setMenuOpen(prev => !prev)}
        onProductClick={setSelectedProduct}
        isScrolling={isScrolling && activeTab === "home" && !selectedProduct}
      />
      <main className="pb-44 lg:pb-16 pt-[72px] lg:pt-[88px] flex-1 relative z-10 w-full min-h-[100vh]">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="w-full min-h-[100vh] block"
          >
            <Suspense fallback={
              <div className="max-w-7xl mx-auto px-4 py-6 min-h-[70vh]">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 p-2 bg-neutral-100/30 dark:bg-white/[0.02] rounded-2xl min-h-[50vh]">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <ProductCardSkeleton key={`suspense-skeleton-${i}`} />
                  ))}
                </div>
              </div>
            }>
            {activeTab === "home" && (
              <HomeView onView={setSelectedProduct} />
            )}
            {activeTab === "track" && <TrackOrderView />}
            {activeTab === "search" && <SearchView onView={setSelectedProduct} />}
            {activeTab === "categories" && (
              <CategoriesView onSelectCategory={setSelectedCat} />
            )}
            {activeTab === "deals" && <DealsView onView={setSelectedProduct} />}
            {activeTab === "free_delivery" && (
              <FreeDeliveryView onView={setSelectedProduct} />
            )}
            {activeTab === "orders" && <OrdersView />}
            {activeTab === "admin" &&
              (isCmsAuthorized ? (
                <ProtectedRoute allowedRoles={['admin', 'editor', 'seller']}><Suspense fallback={<div />}><AdminPanelView onBackToUserView={() => setActiveTab("home")} /></Suspense></ProtectedRoute>
              ) : (
                <div className="max-w-md mx-auto px-4 py-16 flex flex-col items-center justify-center min-h-[60vh] text-center space-y-6">
                  <div className="w-full bg-white/[0.02] border border-primary/25 backdrop-blur-xl p-8 rounded-3xl text-center space-y-6 shadow-[0_12px_40px_rgba(var(--theme-rgb),0.08)]">
                    <div className="h-16 w-16 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center mx-auto animate-pulse">
                      <svg
                        className="w-8 h-8 text-primary stroke-[1.5px]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-xl font-black tracking-tight text-neutral-800 dark:text-white uppercase">
                        {lang === "en"
                          ? "Admin Access Restricted"
                          : "এডমিন অ্যাক্সেস সংরক্ষিত"}
                      </h3>
                      <p className="text-xs text-neutral-400 leading-relaxed">
                        {lang === "en"
                          ? "This area requires verified owner or operator credentials. Log in with your registered Google Admin account."
                          : "এই প্যানেলটি শুধুমাত্র সাজমার্ট অ্যাডমিন বা এডিটরদের জন্য। আপনার অনুমোদিত গুগল অ্যাকাউন্ট দিয়ে লগইন করুন।"}
                      </p>
                    </div>

                    <button
                      onClick={async () => {
                        try {
                          await loginWithGoogle();
                        } catch (e: any) {
                          alert(
                            lang === "en"
                              ? "Google authentication failed: " + e.message
                              : "লগইন ব্যর্থ হয়েছে: " + e.message,
                          );
                        }
                      }}
                      className="w-full flex items-center justify-center gap-2.5 rounded-full py-3.5 bg-primary-gradient hover:brightness-110 text-white font-black text-sm tracking-normal shadow-md shadow-primary/20 transition-all cursor-pointer transform active:scale-95 duration-200"
                    >
                      {lang === "en"
                        ? "Sign in with Authorized Google Account"
                        : "গুগল অ্যাকাউন্ট দিয়ে লগইন"}
                    </button>
                  </div>
                </div>
              ))}
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>
      {!checkoutOpen && (
        <BottomNav
          onTabClick={() => setSelectedProduct(null)}
          isScrolling={isScrolling && activeTab === "home" && !selectedProduct}
        />
      )}
      <Suspense fallback={null}><ProductDetailsModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onBuyNow={(prod) => {
          setSelectedProduct(null);
          setCheckoutOpen(true);
        }}
      /></Suspense>
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        onCheckout={() => setCheckoutOpen(true)}
      />
      <Suspense fallback={null}><CheckoutModal
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
      /></Suspense>
      <MenuDrawer isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Floating AI Skincare Specialist Consultation assistant (Hidden while menu or checkout is open) */}
      {!menuOpen && !checkoutOpen && (
        <AIAdvisor onProductView={setSelectedProduct} />
      )}

      {/* Exit Confirmation Modal */}
      <AnimatePresence>
        {showExitConfirm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-[#1E293B] rounded-3xl p-6 shadow-2xl max-w-sm w-full border border-neutral-200 dark:border-white/10"
            >
              <h3 className="text-xl font-black text-neutral-900 dark:text-white mb-2 tracking-tight">
                {lang === 'en' ? 'Exit Website?' : 'ওয়েবসাইট থেকে বের হবেন?'}
              </h3>
              <p className="text-neutral-600 dark:text-neutral-400 mb-8 text-sm">
                {lang === 'en' ? 'Are you sure you want to leave this website?' : 'আপনি কি সত্যিই ওয়েবসাইট থেকে বের হতে চান?'}
              </p>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    allowExit.current = true;
                    setShowExitConfirm(false);
                    window.history.go(-2);
                    setTimeout(() => {
                      if (allowExit.current) window.close();
                    }, 300);
                  }}
                  className="flex-1 py-3 px-4 rounded-xl bg-neutral-100 dark:bg-white/5 hover:bg-neutral-200 dark:hover:bg-white/10 text-neutral-800 dark:text-white font-bold transition-colors cursor-pointer"
                >
                  {lang === 'en' ? 'Yes' : 'হ্যাঁ'}
                </button>
                <button 
                  onClick={() => setShowExitConfirm(false)}
                  className="flex-1 py-3 px-4 rounded-xl bg-primary hover:bg-primary/90 text-white font-bold transition-colors shadow-lg shadow-primary/25 cursor-pointer"
                >
                  {lang === 'en' ? 'No' : 'না'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
